import { queryKeys } from 'contexts/QueryClient';
import { useInfiniteQuery, useQuery } from 'react-query';
import {
  CollectionEntity,
  CollectionEntities,
  CollectionFetchType,
  CollectionEntityType,
  EntityMetadata,
  CollectionResponse,
  CollectionRaw,
  MetadataResponse,
} from './model';
import { apiClient } from 'utils/API';
import { useUser } from 'hooks/useUser';
import { collectionTransformers } from './transformer';

const COLLECTIONS_RESPONSE_PER_PAGE = 16;

type CategoryData = {
  id: CollectionFetchType;
  displayName: string;
};

export const collectionCategoryData: Record<CollectionFetchType, CategoryData> = {
  [CollectionEntities.Duskbreakers]: {
    id: CollectionEntities.Duskbreakers,
    displayName: 'Duskbreakers',
  },
  [CollectionEntities.Gribbles]: {
    id: CollectionEntities.Gribbles,
    displayName: 'Gribbles',
  },
  [CollectionEntities.Eggs]: {
    id: CollectionEntities.Eggs,
    displayName: 'Gribble Eggs',
  },
  all: {
    id: 'all',
    displayName: 'All',
  },
} as const;

export const DEFAULT_COLLECTION_NFT_CATEGORY: CategoryData = collectionCategoryData.all;

export type CollectionNFTType = typeof collectionCategoryData[keyof typeof collectionCategoryData];

export function toNftType(input?: string): CollectionNFTType {
  if (!input) {
    return DEFAULT_COLLECTION_NFT_CATEGORY;
  }
  const out = collectionCategoryData[input as CollectionFetchType];
  return (out ?? DEFAULT_COLLECTION_NFT_CATEGORY) as CollectionNFTType;
}

async function fetchMetadata(
  type: CollectionEntityType,
  id: string | number,
  userId: string,
): Promise<EntityMetadata> {
  const { data } = await apiClient.get<MetadataResponse>(
    `/users/${userId}/collections/${type}/${id}`,
  );

  return data.item;
}

async function fetchCollection(
  userId: string,
  type: CollectionFetchType,
  lastPage?: CollectionResponse,
): Promise<CollectionResponse> {
  const params = lastPage
    ? {
        pageSize: COLLECTIONS_RESPONSE_PER_PAGE,
        cursor: lastPage.cursor,
        cursorCollection: lastPage.cursorCollection,
      }
    : {};

  const { data } = await apiClient.get<CollectionRaw>(`/users/${userId}/collections`, {
    params: type !== 'all' ? { collection: type, ...params } : params,
  });

  const transformedData = data.items.map((i) => collectionTransformers.toEntity(i));

  return {
    items: transformedData,
    cursor: data.cursor?.value ?? null,
    cursorType: data.cursorCollection,
    collectionTotals: data.collectionTotals,
    cursorCollection: data.cursorCollection,
  };
}

export function useCollection(type: CollectionFetchType) {
  const { account } = useUser();
  const userId = account.dusktech?.userId!;

  return useInfiniteQuery(
    queryKeys.collection(type),
    ({ pageParam }) => fetchCollection(userId, type, pageParam),
    {
      // cursor is `null` when there's no more items to paginate
      getNextPageParam: (resp) => {
        // if we get fewer items than expected we shouldn't try to
        // fetch more since we're probably at the end. The backend
        // doesn't notify the client of this
        return resp.cursor && resp.items.length >= COLLECTIONS_RESPONSE_PER_PAGE ? resp : undefined;
      },
    },
  );
}

export function useCollectionEntityMetadata(entity?: CollectionEntity) {
  const { account } = useUser();
  const userId = account.dusktech?.userId!;

  return useQuery(
    entity ? queryKeys.collectionEntityMetadata(entity.type, entity.item.id) : [],
    // function is only called when the query is enabled
    () => fetchMetadata(entity!.type, entity!.item.id, userId),
    { enabled: Boolean(entity), retry: false },
  );
}

export type CollectionFilter = {
  type: CollectionFetchType;
  /// undefined if not loaded
  count?: number;
};
